import { z } from 'zod';

import { type FilterChip } from '@apps/web/components/ui/filter_bar';

export const PER_PAGE = 50;

export const CALL_SOURCE_VALUES = ['attio', 'granola', 'recall'] as const;
export type CallSource = (typeof CALL_SOURCE_VALUES)[number];

export const CALL_SOURCE_LABELS: Record<CallSource, string> = {
    attio: 'Attio',
    granola: 'Granola',
    recall: 'Recall',
};

// Display label for a call's source. Known sources use their canonical label;
// any future/unknown source kind falls back to a capitalized form.
export function sourceLabel(sourceKind: string): string {
    return labelOrCapitalize(CALL_SOURCE_LABELS, sourceKind);
}

// The worker pipeline is the source of truth for the taxonomy; a slug missing
// here still renders (capitalized fallback) but can never be offered in the
// filter.
export const ANALYSIS_CALL_TYPE_VALUES = [
    'sales',
    'deployment-cs',
    'hiring',
    'internal',
    'partner-vendor',
    'other',
] as const;
export type AnalysisCallType = (typeof ANALYSIS_CALL_TYPE_VALUES)[number];

export const ANALYSIS_CALL_TYPE_LABELS: Record<AnalysisCallType, string> = {
    sales: 'Sales',
    'deployment-cs': 'Deployment / CS',
    hiring: 'Hiring',
    internal: 'Internal',
    'partner-vendor': 'Partner / Vendor',
    other: 'Other',
};

// Display label for a classified call type. Known types use their canonical
// label; any future/unknown type falls back to a capitalized form.
export function analysisCallTypeLabel(callType: string): string {
    return labelOrCapitalize(ANALYSIS_CALL_TYPE_LABELS, callType);
}

function labelOrCapitalize(
    labels: Partial<Record<string, string>>,
    value: string,
): string {
    return (
        labels[value] ??
        (value ? value.charAt(0).toUpperCase() + value.slice(1) : value)
    );
}

export const ANALYZED_FILTER_VALUES = ['analyzed', 'unanalyzed'] as const;
export type AnalyzedFilter = (typeof ANALYZED_FILTER_VALUES)[number];

export const ANALYZED_FILTER_LABELS: Record<AnalyzedFilter, string> = {
    analyzed: 'Analyzed',
    unanalyzed: 'Not analyzed',
};

// The categorical dimensions rendered through the shared filter bar. Date
// range and the participant/domain text search use dedicated controls because
// they are not "is / is not" choices.
export type CallFilterKey = 'source' | 'type' | 'analyzed';

export const callsSearchSchema = z.object({
    q: z.string().optional(),
    source: z.enum(CALL_SOURCE_VALUES).optional(),
    type: z.enum(ANALYSIS_CALL_TYPE_VALUES).optional(),
    analyzed: z.enum(ANALYZED_FILTER_VALUES).optional(),
    participant: z.string().optional(),
    tag: z.string().optional(),
    started_after: z.string().optional(),
    started_before: z.string().optional(),
    order: z.enum(['asc', 'desc']).optional().default('desc'),
    page: z.coerce.number().optional().default(1),
});

export type CallsSearch = z.infer<typeof callsSearchSchema>;

export function searchToFilterChips(
    source: CallSource | undefined,
    type: AnalysisCallType | undefined,
    analyzed: AnalyzedFilter | undefined,
): Array<FilterChip<CallFilterKey>> {
    return [
        ...(source ? [{ dimension: 'source' as const, values: [source] }] : []),
        ...(type ? [{ dimension: 'type' as const, values: [type] }] : []),
        ...(analyzed
            ? [{ dimension: 'analyzed' as const, values: [analyzed] }]
            : []),
    ];
}

export function filterChipsToSource(
    chips: Array<FilterChip<CallFilterKey>>,
): CallSource | undefined {
    return chipValue(chips, 'source', CALL_SOURCE_VALUES);
}

export function filterChipsToType(
    chips: Array<FilterChip<CallFilterKey>>,
): AnalysisCallType | undefined {
    return chipValue(chips, 'type', ANALYSIS_CALL_TYPE_VALUES);
}

export function filterChipsToAnalyzed(
    chips: Array<FilterChip<CallFilterKey>>,
): AnalyzedFilter | undefined {
    return chipValue(chips, 'analyzed', ANALYZED_FILTER_VALUES);
}

function chipValue<T extends string>(
    chips: Array<FilterChip<CallFilterKey>>,
    dimension: CallFilterKey,
    values: ReadonlyArray<T>,
): T | undefined {
    const value = chips.find((chip) => chip.dimension === dimension)?.values[0];
    return value !== undefined &&
        (values as ReadonlyArray<string>).includes(value)
        ? (value as T)
        : undefined;
}
